'use client';

// ChatMessage - Consumer-specific wrapper around @boty/ui ChatBubble
// Adds relative timestamp formatting using consumer translations
// Story 4.4: Adds feedback buttons below bot messages

import { useEffect, useState } from 'react';
import { ChatBubble, type Message } from '@boty/ui';
import { useTranslations } from 'next-intl';
import { FeedbackButtons, type FeedbackRating } from './FeedbackButtons';

// Re-render every 30s so relative timestamps stay current ("Just now" → "2 min ago")
// without requiring a page refresh or new message arrival.
function useTimeTick(): number {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 30_000);
    return () => clearInterval(id);
  }, []);
  return tick;
}

export interface ChatMessageProps {
  /** Message data */
  message: Message;
  /** Whether to show timestamp */
  showTimestamp?: boolean;
  /** Current feedback rating for this message (from useFeedback hook) */
  feedbackRating?: FeedbackRating | undefined;
  /** Callback when user submits feedback */
  onFeedbackSubmit?: (
    messageId: string,
    rating: FeedbackRating,
    comment?: string
  ) => void;
  /** Callback when a link in the message is clicked */
  onLinkClick?: (url: string) => void;
}

/**
 * Calculate relative time components for i18n formatting
 */
function getRelativeTimeInfo(isoString: string): {
  type: 'justNow' | 'minutesAgo' | 'time';
  minutes?: number;
  timeString?: string;
} {
  try {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));

    // "Just now" for less than 1 minute
    if (diffMinutes < 1) {
      return { type: 'justNow' };
    }
    // "X min ago" for less than 60 minutes
    if (diffMinutes < 60) {
      return { type: 'minutesAgo', minutes: diffMinutes };
    }
    // Otherwise show time (e.g., "10:23 AM")
    return {
      type: 'time',
      timeString: date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }),
    };
  } catch {
    return { type: 'time', timeString: '' };
  }
}

/**
 * ChatMessage - Display a single chat message with WhatsApp-style bubbles
 *
 * Uses @boty/ui ChatBubble for consistent styling.
 *
 * Features:
 * - User messages: indigo background, white text, right-aligned (swaps in RTL)
 * - Bot messages: light gray background, dark text, ALWAYS left-aligned (WhatsApp pattern)
 * - Relative timestamps ("Just now", "2 min ago") using consumer translations
 * - Streaming text indicator
 */
export function ChatMessage({
  message,
  showTimestamp = true,
  feedbackRating,
  onFeedbackSubmit,
  onLinkClick,
}: ChatMessageProps) {
  const t = useTranslations('consumer.chat');
  const isUser = message.role === 'user';
  // Subscribe to a 30s tick so timestamps recompute periodically.
  // The variable is intentionally only consumed via re-render.
  useTimeTick();

  // Format timestamp using i18n
  const formatTimestamp = (isoString: string): string => {
    const info = getRelativeTimeInfo(isoString);
    switch (info.type) {
      case 'justNow':
        return t('timestamp.justNow');
      case 'minutesAgo':
        return t('timestamp.minutesAgo', { count: info.minutes ?? 1 });
      case 'time':
      default:
        return info.timeString || '';
    }
  };

  const timestamp = showTimestamp && message.createdAt ? formatTimestamp(message.createdAt) : undefined;
  const isStreaming = message.isStreaming && !isUser;

  // Show feedback only on completed (non-streaming) bot messages
  const showFeedback = !isUser && !message.isStreaming && onFeedbackSubmit;

  return (
    <>
      <ChatBubble
        sender={isUser ? 'user' : 'bot'}
        {...(timestamp ? { timestamp } : {})}
        {...(isStreaming ? { isStreaming: true } : {})}
        {...(!isUser ? { markdown: true, onLinkClick } : {})}
        sx={{ mb: showFeedback ? 0 : 2 }}
      >
        {message.content}
      </ChatBubble>
      {showFeedback && (
        <FeedbackButtons
          messageId={message.id}
          {...(feedbackRating ? { currentRating: feedbackRating } : {})}
          onFeedbackSubmit={onFeedbackSubmit}
        />
      )}
      {showFeedback && <div style={{ marginBottom: 16 }} />}
    </>
  );
}

export default ChatMessage;
